import { Component, OnInit, ViewChild } from "@angular/core";
import { CashPaymentWorkflowComponent } from "../cash-payment-workflow/cash-payment-workflow.component";
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Errors, LocalService, ManagerService } from "../../../../core";
import { TranslateService } from "@ngx-translate/core";
import { StepItem, StepSummary } from "src/app/shared/ui/stepper/stepper.component";

@Component({
  selector: "app-account-rewards-content",
  templateUrl: "./account-rewards-content.component.html",
  styleUrls: ["./account-rewards-content.component.scss"]
})
export class AccountRewardsContentComponent implements OnInit {
  errors: Errors = { errors: {} };
  userProfile: any;
  rebateContactForm!: FormGroup;

  companyPreferencesData: any[] = [];
  preferencesIds: any[] = [];

  activePanel: string = 'claimingType';
  reachedStep: number = 1;
  isManualDistribution: boolean = false;
  isPamUser: boolean = false;
  steps: StepItem[] = [];

  radioselection: string = "";
  pointsIconSvg: string = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="6"></circle><path d="M18.09 10.37A6 6 0 1 1 10.34 18"></path><path d="M7 6h1v4"></path><path d="m16.71 13.88.7.71-2.82 2.82"></path></svg>';
  cashIconSvg: string = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-dollar-sign h-5 w-5"><circle cx="12" cy="12" r="10"></circle><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path><path d="M12 18V6"></path></svg>';
  selectSellerClaimingMethod: string = "";
  companyPreferenceId: number = 0;
  companyParams: any[] = [];
  rewardRadioSelection: string = "";
  rewardTypeSavebtn: boolean = true;
  rewardTypeRadioBtn: boolean = false;
  optInCash: any = null;

  @ViewChild(CashPaymentWorkflowComponent) cashPaymentWorkflowComponent!: CashPaymentWorkflowComponent;

  isSuccessModalOpen: boolean = false;

  constructor(
    private managerService: ManagerService,
    private translate: TranslateService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.userProfile = LocalService.get("userProfile");
    this.isPamUser = this.userProfile.hasOwnProperty('userType') && this.userProfile.userType === 'PAM';
    this.initializeSteps();
    this.initializeRebateForm();
    this.getCompanyPreferences();
  }

  private initializeRebateForm(): void {
    this.rebateContactForm = this.formBuilder.group({
      primaryContactForm: this.getPrimaryRebateDetails(),
      backupContactForm: this.backupRebateContactDetails(),
    });
  }

  private getPrimaryRebateDetails(): FormGroup {
    const domain = this.getDomain(this.userProfile.primaryEmail);
    return this.formBuilder.group({
      id: [null],
      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      email: ["", [Validators.required, Validators.email, this.emailContainsAtSymbol, this.domainValidator(domain)]],
    });
  }

  private backupRebateContactDetails(): FormGroup {
    const domain = this.getDomain(this.userProfile.primaryEmail);
    return this.formBuilder.group({
      id: [null],
      backupFirstName: [null],
      backupLastName: [null],
      backupEmail: [null, [this.emailContainsAtSymbol, this.domainValidator(domain)]],
    });
  }

  private getDomain(email: string): string {
    const match = email ? email.match(/@(.+)$/) : null;
    return match ? match[1] : "";
  }

  private domainValidator(domain: string) {
    return (control: { value: string }) => {
      if (!control.value) { return null; }
      return control.value.endsWith(domain) ? null : { domain: true };
    };
  }

  private emailContainsAtSymbol(control: AbstractControl) {
    const email = control.value;
    if (email && email.indexOf("@") === -1) {
      return { invalidEmail: 'Email must contain an "@" symbol' };
    }
    return null;
  }

  getCompanyPreferences(): void {
    if (this.userProfile.company && this.userProfile.company.id) {
      this.managerService.getCompanyPreferences({ companyId: this.userProfile.company.id }).subscribe(
        (data: any) => {
          this.preferencesIds = data.companyPreferences.reduce(
            (activePreferences: any[], item: any) => {
              if (item.enabled) { activePreferences.push(item.id); }
              return activePreferences;
            },
            []
          );
          this.companyPreferencesData = data.companyPreferences.map((item: any) => item.enabled);
          if (this.companyPreferencesData[7] === true) {
            this.radioselection = "Company Administrators";
          } else if (this.companyPreferencesData[8] === true) {
            this.radioselection = "Seller Individuals";
          }
          if (this.companyPreferencesData[3] === true) {
            this.selectSellerClaimingMethod = 'Quick Claiming';
          } else if (this.companyPreferencesData[4] === true) {
            this.selectSellerClaimingMethod = 'POP Claiming';
          }
          const sellerConfigured = this.companyPreferencesData[8] === true && this.companyPreferencesData[9] === true;
          const adminConfigured = this.companyPreferencesData[7] === true && this.companyPreferencesData[10] === true;
          if (sellerConfigured || adminConfigured) {
            this.activePanel = 'rewardType';
            this.reachedStep = 4;
            if (this.companyPreferencesData[11] === true) {
              this.rewardRadioSelection = 'Points';
              this.rewardTypeSavebtn = false;
            } else if (this.companyPreferencesData[12] === true) {
              this.rewardRadioSelection = 'Cash';
              this.rewardTypeSavebtn = true;
              this.loadRebateCoordinatorData();
            } else {
              this.rewardRadioSelection = 'Points';
              this.rewardTypeSavebtn = false;
            }
          }
        },
        (err: any) => { this.errors = err; }
      );
    }
  }

  onPartnerChange(): void {
    this.activePanel = 'claimingType';
  }

  onClaimMethodChange(partnerName: string, method: string): void {
    if (partnerName === "" && this.companyPreferencesData[7] === true) {
      partnerName = "Company Administrators";
    }
    if (partnerName === "" && this.companyPreferencesData[8] === true) {
      partnerName = "Seller Individuals";
    }
    if (partnerName === "Company Administrators") {
      this.radioselection = "Company Administrators";
      this.activePanel = 'claimingMethod';
      this.selectSellerClaimingMethod = "Quick Claiming";
    } else if (partnerName === "Seller Individuals") {
      this.activePanel = 'sellerClaimingMethod';
      this.selectSellerClaimingMethod = method.indexOf("POP Claiming") > -1 ? "POP Claiming" : "Quick Claiming";
    }
  }

  selectClaimSubmitter(): void {
    if (this.radioselection === "Company Administrators") {
      this.activePanel = 'claimingMethod';
      this.reachedStep = 2;
      this.selectSellerClaimingMethod = "Quick Claiming";
    } else if (this.radioselection === "Seller Individuals") {
      this.activePanel = 'sellerClaimingMethod';
      this.reachedStep = 2;
      this.selectSellerClaimingMethod = "Quick Claiming";
    }
  }

  onDistributionChange(): void {
    this.activePanel = this.isManualDistribution ? 'adminDistribution' : 'sellerDistribution';
  }

  onRewardTypeSelection(): void {
    this.activePanel = 'rewardType';
  }

  submitClaimMethod(): void {
    this.companyPreferenceId = this.selectSellerClaimingMethod === "POP Claiming" ? 5 : 4;
    this.isManualDistribution = false;
    this.activePanel = 'sellerDistribution';
    this.reachedStep = 3;
  }

  submitDistMethod(): void {
    this.activePanel = 'rewardType';
    this.reachedStep = 4;
    if (this.companyPreferencesData[11] === true) {
      this.rewardRadioSelection = "Points";
    } else if (this.companyPreferencesData[12] === true) {
      this.rewardRadioSelection = "Cash";
      this.rewardTypeSavebtn = true;
      this.loadRebateCoordinatorData();
    }
  }

  changePartnerAdmin(value: string): void {
    this.radioselection = value;
  }

  changeCompanyClaimingTypePreferences(companyPreferencesId: number): void {
    if (this.radioselection === "Company Administrators" && this.selectSellerClaimingMethod === "Quick Claiming") {
      this.companyParams = [
        { companyId: this.userProfile.company.id, preferenceId: 11, enabled: true },
        { companyId: this.userProfile.company.id, preferenceId: 14, enabled: true },
        { companyId: this.userProfile.company.id, preferenceId: 4, enabled: true },
      ];
    } else {
      this.companyParams = [
        { companyId: this.userProfile.company.id, preferenceId: 12, enabled: true },
        { companyId: this.userProfile.company.id, preferenceId: 13, enabled: true },
        { companyId: this.userProfile.company.id, preferenceId: this.selectSellerClaimingMethod === "Quick Claiming" ? 4 : 5, enabled: true },
        { companyId: this.userProfile.company.id, preferenceId: this.rewardRadioSelection === "Points" ? 15 : 16, enabled: true },
      ];
    }

    if (this.rewardRadioSelection === "Cash") {
      const rebateContactReq = this.getRebateContactFormData();
      if (rebateContactReq != null) {
        this.managerService.addRebateCoordinators(rebateContactReq).subscribe(() => {});
      }
    }

    this.managerService.postcompanyListPreferences(this.companyParams).subscribe(
      (data: any) => {
        this.reachedStep = 4;
        this.getCompanyPreferences();
        this.isSuccessModalOpen = true;
      },
      (err: any) => { this.errors = err; }
    );
  }

  closeSuccessModal(): void {
    this.isSuccessModalOpen = false;
  }

  submitAdminClaimMethod(): void {
    this.isManualDistribution = true;
    this.companyPreferenceId = 11;
    this.activePanel = 'adminDistribution';
    this.reachedStep = 3;
  }

  onSubmitButtonStateChanged(isDisabled: boolean): void {
    this.rewardTypeSavebtn = isDisabled;
  }

  getRebateContactFormData(): any {
    if (this.rebateContactForm == null) {
      return null;
    }
    const formData = this.rebateContactForm.value;
    const contactData: any[] = [];

    if (formData.primaryContactForm.firstName && formData.primaryContactForm.email) {
      contactData.push({
        id: formData.primaryContactForm.id,
        firstName: formData.primaryContactForm.firstName,
        lastName: formData.primaryContactForm.lastName,
        email: formData.primaryContactForm.email,
        companyId: this.userProfile.company.id,
        priority: 1,
      });
    }

    if (formData.backupContactForm.backupFirstName != null ||
        formData.backupContactForm.backupLastName != null ||
        formData.backupContactForm.backupEmail != null) {
      contactData.push({
        id: formData.backupContactForm.id,
        firstName: formData.backupContactForm.backupFirstName,
        lastName: formData.backupContactForm.backupLastName,
        email: formData.backupContactForm.backupEmail,
        companyId: this.userProfile.company.id,
        priority: 2,
      });
    }

    return contactData;
  }

  changeRewardType(eve: string): void {
    this.rewardRadioSelection = eve;
    if (eve === "Cash") {
      this.rewardTypeSavebtn = true;
      this.loadRebateCoordinatorData();
    } else {
      this.rewardTypeSavebtn = false;
    }
  }

  private loadRebateCoordinatorData(): void {
    const compId = "companyId=" + this.userProfile.company.id;
    this.managerService.getRebateCoordinators(compId).subscribe((response: any) => {
      if (response && response.length > 0) {
        const primaryForm = this.rebateContactForm.get("primaryContactForm");
        if (primaryForm && response[0]) {
          primaryForm.patchValue({
            firstName: response[0].firstName,
            lastName: response[0].lastName,
            email: response[0].email,
            id: response[0].id,
          });
        }
        const backupForm = this.rebateContactForm.get("backupContactForm");
        if (backupForm && response[1]) {
          backupForm.patchValue({
            backupFirstName: response[1].firstName,
            backupLastName: response[1].lastName,
            backupEmail: response[1].email,
            id: response[1].id,
          });
        }
      }
    });
  }

  private initializeSteps(): void {
    this.steps = [
      { id: 'claimingType', title: this.translate.instant('manageActivity.optionstype1') },
      { id: 'claimingMethod', title: this.translate.instant('manageActivity.optionstype2') },
      { id: 'distributionMethod', title: this.translate.instant('manageActivity.optionstype3') },
      { id: 'rewardType', title: this.translate.instant('manageActivity.optionStype4') },
    ];
  }

  get currentStepIndex(): number {
    return this.reachedStep - 1;
  }

  get stepSummaryData(): { [key: string]: StepSummary } {
    return {
      claimingType: {
        label: '',
        value: this.radioselection === 'Seller Individuals'
          ? this.translate.instant('manageActivity.radio1')
          : this.radioselection === 'Company Administrators'
            ? this.translate.instant('manageActivity.radio2')
            : this.translate.instant('manageActivity.select'),
        status: this.reachedStep >= 2 ? 'completed' : 'active'
      },
      claimingMethod: {
        label: '',
        value: this.selectSellerClaimingMethod === 'POP Claiming'
          ? this.translate.instant('manageActivity.radio4')
          : this.selectSellerClaimingMethod === 'Quick Claiming'
            ? this.translate.instant('manageActivity.radio3')
            : this.translate.instant('manageActivity.select'),
        status: this.reachedStep >= 3 ? 'completed' : this.reachedStep === 2 ? 'active' : 'disabled'
      },
      distributionMethod: {
        label: '',
        value: this.reachedStep >= 3
          ? this.translate.instant(this.isManualDistribution ? 'manageActivity.radio6' : 'manageActivity.radio5')
          : this.translate.instant('manageActivity.select'),
        status: this.reachedStep >= 4 ? 'completed' : this.reachedStep === 3 ? 'active' : 'disabled'
      },
      rewardType: {
        label: '',
        value: this.rewardRadioSelection === 'Cash'
          ? this.translate.instant('manageActivity.radio8')
          : this.rewardRadioSelection === 'Points'
            ? this.translate.instant('manageActivity.radio7')
            : this.translate.instant('manageActivity.select'),
        status: this.reachedStep >= 4 ? 'active' : 'disabled'
      }
    };
  }

  onStepNavClick(index: number): void {
    if (index === 0) this.onPartnerChange();
    else if (index === 1) this.onClaimMethodChange(this.radioselection, this.selectSellerClaimingMethod);
    else if (index === 2) this.onDistributionChange();
    else if (index === 3) this.onRewardTypeSelection();
  }
}
